import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from './auth/AuthContext';
import '../css/system-settings.css';

export function SystemSettings() {
    const { getToken } = useAuth();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [tenantName, setTenantName] = useState('');
    const [tenantLogoUrl, setTenantLogoUrl] = useState('');
    const [tenantLogoPreview, setTenantLogoPreview] = useState('');
    const [selectedLogoFileName, setSelectedLogoFileName] = useState('');
    const [savingBranding, setSavingBranding] = useState(false);
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [draftRoles, setDraftRoles] = useState({});
    const [savingUserId, setSavingUserId] = useState('');
    const [togglingDeactivationUserId, setTogglingDeactivationUserId] = useState('');
    const [roleAudit, setRoleAudit] = useState([]);

    const fetchRoleAudit = async () => {
        const token = getToken();
        const auditRes = await fetch('/api/system/role-audit?limit=150', {
            headers: { Authorization: `Bearer ${token}` }
        });
        if (auditRes.ok) {
            const auditPayload = await auditRes.json();
            setRoleAudit(Array.isArray(auditPayload) ? auditPayload : []);
        } else {
            setRoleAudit([]);
        }
    };

    const fetchUsers = async () => {
        setLoading(true);
        setError('');
        try {
            const token = getToken();
            const [usersRes, brandingRes] = await Promise.all([
                fetch('/api/system/users', {
                    headers: { Authorization: `Bearer ${token}` }
                }),
                fetch('/api/public/tenant-branding')
            ]);

            if (!usersRes.ok) {
                throw new Error(`Failed to load users (${usersRes.status})`);
            }

            const payload = await usersRes.json();
            const loadedRoles = Array.isArray(payload.roles) ? payload.roles : [];
            const loadedUsers = Array.isArray(payload.users) ? payload.users : [];
            setRoles(loadedRoles);
            setUsers(loadedUsers);
            const initialDraft = {};
            loadedUsers.forEach(user => {
                initialDraft[user.id] = Array.isArray(user.roles) ? user.roles : [];
            });
            setDraftRoles(initialDraft);

            if (brandingRes.ok) {
                const branding = await brandingRes.json().catch(() => ({}));
                setTenantName(String(branding?.tenantName || '').trim());
                setTenantLogoUrl(String(branding?.logoUrl || '').trim());
                setTenantLogoPreview('');
                setSelectedLogoFileName('');
            }

            await fetchRoleAudit();
        } catch (err) {
            setError(err.message || 'Failed to load system users.');
        } finally {
            setLoading(false);
        }
    };

    const saveTenantBranding = async () => {
        setSavingBranding(true);
        setError('');
        try {
            const token = getToken();
            const payloadBody = tenantLogoPreview
                ? { logoDataUrl: tenantLogoPreview }
                : { logoUrl: tenantLogoUrl.trim() || null };
            const res = await fetch('/api/tenant/branding', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(payloadBody)
            });
            if (!res.ok) {
                const payload = await res.json().catch(() => ({}));
                const detail = payload?.code ? ` [${payload.code}]` : '';
                throw new Error(`${payload.message || 'Failed to save tenant branding.'} (${res.status}${detail})`);
            }

            const payload = await res.json().catch(() => ({}));
            setTenantName(String(payload?.tenantName || tenantName).trim());
            setTenantLogoUrl(String(payload?.logoUrl || '').trim());
            setTenantLogoPreview('');
            setSelectedLogoFileName('');
        } catch (err) {
            setError(err.message || 'Failed to save tenant branding.');
        } finally {
            setSavingBranding(false);
        }
    };

    const clearTenantBranding = async () => {
        setSavingBranding(true);
        setError('');
        try {
            const token = getToken();
            const res = await fetch('/api/tenant/branding', {
                method: 'DELETE',
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            if (!res.ok) {
                const payload = await res.json().catch(() => ({}));
                const detail = payload?.code ? ` [${payload.code}]` : '';
                throw new Error(`${payload.message || 'Failed to clear tenant branding.'} (${res.status}${detail})`);
            }
            setTenantLogoUrl('');
            setTenantLogoPreview('');
            setSelectedLogoFileName('');
        } catch (err) {
            setError(err.message || 'Failed to clear tenant branding.');
        } finally {
            setSavingBranding(false);
        }
    };

    const handleLogoFileSelected = async (event) => {
        const file = event.target.files && event.target.files[0];
        if (!file) return;

        const isImage = /^image\//i.test(file.type || '');
        if (!isImage) {
            setError('Please select an image file.');
            return;
        }

        if (file.size > 1200000) {
            setError('Logo file is too large. Please use an image under 1.2 MB.');
            return;
        }

        const readAsDataUrl = () => new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(String(reader.result || ''));
            reader.onerror = () => reject(new Error('Failed to read selected file.'));
            reader.readAsDataURL(file);
        });

        try {
            setError('');
            const dataUrl = await readAsDataUrl();
            if (!dataUrl.startsWith('data:image/')) {
                throw new Error('Unsupported image format.');
            }
            setTenantLogoPreview(dataUrl);
            setSelectedLogoFileName(file.name || 'selected logo');
        } catch (err) {
            setError(err.message || 'Failed to prepare logo file.');
        }
    };

    useEffect(() => {
        fetchUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const sortedUsers = useMemo(() => {
        return [...users].sort((a, b) => String(a.email || '').localeCompare(String(b.email || '')));
    }, [users]);

    const toggleRole = async (userId, role) => {
        if (savingUserId === userId || togglingDeactivationUserId === userId) {
            return;
        }

        const current = draftRoles[userId] || [];
        const hasRole = current.includes(role);
        const next = hasRole ? current.filter(r => r !== role) : [...current, role];
        setDraftRoles(prev => ({ ...prev, [userId]: next }));

        await saveRoles(userId, next, current);
    };

    const saveRoles = async (userId, rolesToSave, previousRoles = null) => {
        setSavingUserId(userId);
        setError('');
        try {
            const token = getToken();
            const res = await fetch(`/api/system/users/${encodeURIComponent(userId)}/roles`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ roles: rolesToSave || [] })
            });
            if (!res.ok) {
                const payload = await res.json().catch(() => ({}));
                throw new Error(payload.message || `Failed to save roles (${res.status})`);
            }

            setUsers(prev => prev.map(user => (
                user.id === userId
                    ? { ...user, roles: rolesToSave || [] }
                    : user
            )));

            await fetchRoleAudit();
        } catch (err) {
            if (Array.isArray(previousRoles)) {
                setDraftRoles(prev => ({ ...prev, [userId]: previousRoles }));
            }
            setError(err.message || 'Failed to save roles.');
        } finally {
            setSavingUserId('');
        }
    };

    const setDeactivated = async (userId, deactivated) => {
        setTogglingDeactivationUserId(userId);
        setError('');
        try {
            const token = getToken();
            const res = await fetch(`/api/system/users/${encodeURIComponent(userId)}/deactivate`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ deactivated })
            });
            if (!res.ok) {
                const payload = await res.json().catch(() => ({}));
                throw new Error(payload.message || `Failed to update account status (${res.status})`);
            }
            await fetchUsers();
        } catch (err) {
            setError(err.message || 'Failed to update account status.');
        } finally {
            setTogglingDeactivationUserId('');
        }
    };

    if (loading) {
        return <p><em>Loading...</em></p>;
    }

    return (
        <div className="system-settings-page" style={{ maxWidth: '1400px', marginLeft: 'auto', marginRight: 'auto', padding: '0 1rem' }}>
            <div className="system-settings-hero">
                <h1>System Settings</h1>
                <p>Manage user roles. Roles can be pre-assigned by email before a user signs up — they will automatically receive the correct access on first login.</p>
            </div>

            {error && <div className="ss-alert alert alert-danger">{error}</div>}

            <div className="ss-card">
                <div className="ss-card-header">
                    <h2>Tenant Branding</h2>
                </div>
                <div className="ss-card-body">
                    <div className="ss-branding-panel">
                    <div className="ss-branding-meta">
                        {tenantName ? `Tenant: ${tenantName}` : 'Tenant branding'}
                    </div>
                    <label className="form-label ss-branding-label" htmlFor="tenant-logo-file">Upload logo image</label>
                    <div className="ss-branding-controls">
                        <input
                            id="tenant-logo-file"
                            type="file"
                            className="form-control ss-branding-file-input"
                            accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
                            onChange={handleLogoFileSelected}
                            disabled={savingBranding}
                        />
                        <div className="ss-branding-actions">
                        <button
                            className="btn btn-primary"
                            onClick={saveTenantBranding}
                            disabled={savingBranding || (!tenantLogoPreview && !tenantLogoUrl.trim())}
                            type="button"
                        >
                            {savingBranding ? 'Saving...' : 'Upload Logo'}
                        </button>
                        <button
                            className="btn btn-outline-secondary"
                            onClick={clearTenantBranding}
                            disabled={savingBranding || (!tenantLogoUrl && !tenantLogoPreview)}
                            type="button"
                        >
                            Clear Logo
                        </button>
                        </div>
                    </div>
                    {selectedLogoFileName ? (
                        <div className="ss-branding-selected form-text">Selected file: {selectedLogoFileName}</div>
                    ) : null}
                    {(tenantLogoPreview || tenantLogoUrl) ? (
                        <div className="ss-branding-preview-wrap">
                            <img
                                src={tenantLogoPreview || tenantLogoUrl}
                                alt="Tenant logo preview"
                                className="ss-branding-preview"
                            />
                        </div>
                    ) : null}
                    </div>
                </div>
            </div>

            <div className="ss-card">
                <div className="ss-card-header">
                    <h2>Users & Roles</h2>
                </div>
                <div className="ss-card-body">
                    <div className="ss-table-wrapper">
                        <table className="ss-table ss-users-table table">
                            <thead>
                                <tr>
                                    <th style={{ width: '30%' }}>User</th>
                                    <th style={{ width: '18%' }}>Email</th>
                                    <th style={{ width: '12%' }}>Created</th>
                                    <th style={{ width: '14%' }}>Last Login</th>
                                    <th style={{ width: '10%' }}>Status</th>
                                    <th style={{ width: '12%' }}>Roles</th>
                                    <th style={{ width: '4%' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedUsers.map(user => {
                                    const selectedRoles = draftRoles[user.id] || [];
                                    return (
                                        <tr key={user.id}>
                                            <td data-label="User">
                                                <div className="ss-user-section">
                                                    <div className="ss-user-name">{user.name || '-'}</div>
                                                    <div className="ss-user-detail">{user.id}</div>
                                                </div>
                                            </td>
                                            <td data-label="Email">{user.email || '-'}</td>
                                            <td data-label="Created">{user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '-'}</td>
                                            <td data-label="Last Login">{user.lastSignInAt ? new Date(user.lastSignInAt).toLocaleString() : 'Never'}</td>
                                            <td data-label="Status">
                                                <span className={user.isDeactivated ? 'ss-status-deactivated' : 'ss-status-active'}>
                                                    {user.isDeactivated ? 'Deactivated' : 'Active'}
                                                </span>
                                            </td>
                                            <td data-label="Roles">
                                                <div className="ss-role-cell">
                                                    <div className="ss-role-controls">
                                                        {roles.map(role => (
                                                            <div key={`${user.id}-toggle-${role}`} className="form-check">
                                                                <input
                                                                    type="checkbox"
                                                                    className="form-check-input"
                                                                    id={`${user.id}-${role}`}
                                                                    checked={selectedRoles.includes(role)}
                                                                    onChange={() => {
                                                                        toggleRole(user.id, role);
                                                                    }}
                                                                    disabled={savingUserId === user.id || togglingDeactivationUserId === user.id}
                                                                />
                                                                <label className="form-check-label" htmlFor={`${user.id}-${role}`}>
                                                                    {role}
                                                                </label>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="ss-actions-cell" data-label="Actions">
                                                <div className="ss-action-group">
                                                    {savingUserId === user.id && (
                                                        <span className="ss-saving-text">Saving roles...</span>
                                                    )}
                                                    <button
                                                        className={user.isDeactivated ? 'btn btn-reactivate btn-success' : 'btn btn-deactivate btn-danger'}
                                                        onClick={() => setDeactivated(user.id, !user.isDeactivated)}
                                                        disabled={savingUserId === user.id || togglingDeactivationUserId === user.id}
                                                    >
                                                        {togglingDeactivationUserId === user.id
                                                            ? (user.isDeactivated ? 'Reactivating...' : 'Deactivating...')
                                                            : (user.isDeactivated ? 'Reactivate' : 'Deactivate')}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div className="ss-card ss-audit-card">
                <div className="ss-card-header">
                    <h2>Role Change Audit</h2>
                </div>
                <div className="ss-card-body">
                    <div className="ss-table-wrapper">
                        <table className="ss-table ss-audit-table table">
                            <thead>
                                <tr>
                                    <th style={{ width: '18%' }}>When</th>
                                    <th style={{ width: '22%' }}>Actor</th>
                                    <th style={{ width: '22%' }}>Target</th>
                                    <th style={{ width: '19%' }}>Previous Roles</th>
                                    <th style={{ width: '19%' }}>New Roles</th>
                                </tr>
                            </thead>
                            <tbody>
                                {roleAudit.length === 0 && (
                                    <tr>
                                        <td colSpan={5} style={{ textAlign: 'center', color: 'var(--color-text-muted)', fontStyle: 'italic', paddingTop: '1.5rem', paddingBottom: '1.5rem' }}>
                                            No audit entries yet.
                                        </td>
                                    </tr>
                                )}
                                {roleAudit.map(entry => (
                                    <tr key={entry.id}>
                                        <td className="ss-audit-timestamp">{entry.createdAt ? new Date(entry.createdAt).toLocaleString() : '-'}</td>
                                        <td>
                                            <div className="ss-user-section">
                                                <div className="ss-user-name">{entry.actorName || '-'}</div>
                                                <div className="ss-user-detail">{entry.actorEmail || ''}</div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="ss-user-section">
                                                <div className="ss-user-name">{entry.targetEmail || '-'}</div>
                                                <div className="ss-user-detail">{entry.targetUserId || ''}</div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="ss-audit-badge-group">
                                                {(entry.previousRoles || []).length === 0
                                                    ? <span className="ss-role-none">None</span>
                                                    : (entry.previousRoles || []).map(role => (
                                                        <span key={`${entry.id}-prev-${role}`} className="badge ss-role-badge" style={{ background: '#f3f5f7', color: '#60707f' }}>
                                                            {role}
                                                        </span>
                                                    ))}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="ss-audit-badge-group">
                                                {(entry.newRoles || []).length === 0
                                                    ? <span className="ss-role-none">None</span>
                                                    : (entry.newRoles || []).map(role => (
                                                        <span key={`${entry.id}-new-${role}`} className="badge ss-role-badge">
                                                            {role}
                                                        </span>
                                                    ))}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}
